import { useContext, useState } from "react";
import axios from "../../config/axios";

// importing styles
import styles from "./ShippingAddressScreen.module.scss";

// importing components
import {
  Button,
  Col,
  Collapse,
  Form,
  message,
} from "antd";

import { AuthContext } from "../../Contexts/AuthContext";
import { AuthModalContext } from "../../Contexts/AuthModalContext";
import { CartContext } from "../../Contexts/CartContext";
import CartOrderSummary from "../CartOrderSummary/CartOrderSummary";
import { FloatingCartButton } from "../CartScreen/CartScreen";
import { DeliveryDates } from "./Components/DeliveryDates";
import { AdditionalInformation } from "./Components/AddtionalInformation";
import { ShippingInformation } from "./Components/ShippingInformation";
import { PersonalInformation } from "./Components/PersonalInformation";

const ShippingAddressScreen = ({
  setStep,
  step,
  setShipping,
  setPersonal,
  setAdditional,
  deliveryDate,
  setDeliveryDate,
}) => {
  const cartContext = useContext(CartContext);
  const { isAuthenticated, user } = useContext(AuthContext);
  const { openSignIn } = useContext(AuthModalContext);

  const [personalForm] = Form.useForm();
  const [shippingForm] = Form.useForm();
  const [additionalForm] = Form.useForm();
  const [isAddrSame, setIsAddrSame] = useState(true);

  const [saveLoading, setSaveLoading] = useState(false);
  const [deliveryStatus, setDeliveryStatus] = useState("idle");

  // the delivery date follows the address the parcel goes to, which is the
  // personal form while the customer has not split the two addresses apart
  const personalZip = Form.useWatch("zip_code", personalForm);
  const shippingZip = Form.useWatch("zip_code", shippingForm);
  const deliveryZip = isAddrSame ? personalZip : shippingZip;

  // a served area with nothing left to book cannot be ordered for, so the
  // customer is kept on this step instead of failing at the payment stage.
  // An area no plan covers is let through the way it was before delivery
  // planning existed
  const canLeaveStep = () => {
    if (deliveryStatus === "loading") {
      message.info("Just a moment, we are loading the delivery dates.");
      return false;
    }

    if (deliveryStatus === "full") {
      message.error(
        "All delivery days for your area are fully booked right now, please try again later.",
      );
      return false;
    }

    if (deliveryStatus === "available" && !deliveryDate) {
      message.error("Please pick a delivery date.");
      return false;
    }

    return true;
  };

  const personalOnFinish = (values) => {
    // with a separate shipping address it is shippingOnFinish that moves the
    // step on, so the delivery check belongs there and not here
    if (isAddrSame && !canLeaveStep()) return;

    setPersonal(values);
    setAdditional(additionalForm.getFieldsValue());
    sessionStorage.setItem("personal", JSON.stringify(values));
    sessionStorage.setItem(
      "additional",
      JSON.stringify(additionalForm.getFieldsValue()),
    );

    if (isAddrSame) {
      setShipping(values);
      sessionStorage.setItem("shipping", JSON.stringify(values));
      setStep(3);
    }
  };

  const shippingOnFinish = (values) => {
    if (!canLeaveStep()) return;

    setShipping(values);
    sessionStorage.setItem("shipping", JSON.stringify(values));
    setStep(3);
  };

  const saveUserAddress = () => {
    if (!isAuthenticated) {
      message.error("Please login to save personal and shipping details!");
      openSignIn();
      return;
    }

    const personalFormData = personalForm.getFieldsValue();
    const shippingFormData = isAddrSame
      ? personalFormData
      : shippingForm.getFieldsValue();
    const additionalFormData = additionalForm.getFieldsValue();

    let payload = {
      s_title: shippingFormData.title,
      s_first_name: shippingFormData.first_name,
      s_last_name: shippingFormData.last_name,
      s_street_address: shippingFormData.street_address,
      s_house_no: shippingFormData.house_no,
      s_zip_code: shippingFormData.zip_code,
      s_country: "germany",
      s_state: shippingFormData.state,
      s_city: shippingFormData.city,
      s_email: shippingFormData.email,
      s_phone_number: shippingFormData.phone_number,

      b_title: personalFormData.title,
      b_first_name: personalFormData.first_name,
      b_last_name: personalFormData.last_name,
      b_street_address: personalFormData.street_address,
      b_house_no: personalFormData.house_no,
      b_zip_code: personalFormData.zip_code,
      b_country: "germany",
      b_state: personalFormData.state,
      b_city: personalFormData.city,
      b_email: personalFormData.email,
      b_phone_number: personalFormData.phone_number,

      additional_info_1: additionalFormData.additional_info_1,
      additional_info_2: additionalFormData.additional_info_2,
    };

    setSaveLoading(true);
    axios
      .patch("/users/update", payload, { withCredentials: true })
      .then((response) => {
        message.success("Personal and Shipping Details saved successfully!");
        setSaveLoading(false);
      });
  };

  return (
    <div>
      <div className="d-flex flex-row align-items-start gap-4">
        <Col lg={18}>
          <div
            className={`${styles.shipping_address_wrapper} checkout_collapse`}
          >
            <Collapse
              expandIconPosition={"end"}
              defaultActiveKey={["1", "2", "3", "4"]}
            >
              <Collapse.Panel key="1" header="Personal / Billing Information">
                <PersonalInformation
                  form={personalForm}
                  onFinish={personalOnFinish}
                  isAddrSame={isAddrSame}
                  setIsAddrSame={setIsAddrSame}
                />
              </Collapse.Panel>

              {!isAddrSame && (
                <Collapse.Panel key={"2"} header={"Shipping Information"}>
                  <ShippingInformation
                    form={shippingForm}
                    onFinish={shippingOnFinish}
                  />
                </Collapse.Panel>
              )}

              <Collapse.Panel key={"3"} header={"Additional Information"}>
                <AdditionalInformation form={additionalForm} />
              </Collapse.Panel>

              <Collapse.Panel key={"4"} header={"Delivery Date & Time Slot"}>
                <DeliveryDates
                  zipCode={deliveryZip}
                  value={deliveryDate}
                  onChange={setDeliveryDate}
                  onStatusChange={setDeliveryStatus}
                />
              </Collapse.Panel>
            </Collapse>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                marginTop: "1rem",
                // marginTop: "2rem",
              }}
            >
              <Button
                style={{ padding: "0 5rem" }}
                type={"primary"}
                onClick={saveUserAddress}
                loading={saveLoading}
                disabled={saveLoading}
              >
                Save the address
              </Button>
            </div>

            {/* <Divider /> */}
          </div>
        </Col>
        <Col lg={6} xs={0}>
          <CartOrderSummary
            cartContext={cartContext}
            step={step}
            setStep={setStep}
            personalForm={personalForm}
            shippingForm={shippingForm}
          />
        </Col>

        <FloatingCartButton
          setStep={setStep}
          step={step}
          personalForm={personalForm}
          shippingForm={shippingForm}
        />
      </div>
    </div>
  );
};

export default ShippingAddressScreen;
